import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './LoginForm.css';

interface LoginFormProps {
  onSwitchToRegister?: () => void;
  onLoginSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister, onLoginSuccess }) => {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);

  // Enhanced setErrors with localStorage persistence
  const setErrorsWithDebug = (newErrors: string[], source: string = 'unknown') => {
    console.log(`🔥 setErrors called from: ${source}`, newErrors);
    
    // Save to localStorage for persistence across re-mounts
    try {
      localStorage.setItem('loginErrors', JSON.stringify(newErrors));
    } catch (e) {
      console.warn('Failed to save errors to localStorage:', e);
    }
    
    setErrors(newErrors);
  };

  // Clear localStorage on mount to remove stale errors
  useEffect(() => {
    localStorage.removeItem('loginErrors');
  }, []);

  // DEBUG: Track errors state changes
  useEffect(() => {
    console.log('🔴 Errors state updated:', errors);
    if (errors.length > 0) {
      console.log('🔴 Should render error box NOW!');
    }
  }, [errors]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing again after seeing an error
    if (errors.length > 0) {
      console.log('🧹 Clearing errors due to user input');
      setErrorsWithDebug([], 'user_input_change');
      setShowResendButton(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.email.trim()) {
      newErrors.push('E-Mail-Adresse ist erforderlich');
    } else if (!isValidEmail(formData.email)) {
      newErrors.push('Bitte geben Sie eine gültige E-Mail-Adresse ein');
    }

    if (!formData.password.trim()) {
      newErrors.push('Passwort ist erforderlich');
    }

    console.log('🟡 validateForm called, found errors:', newErrors);
    // Only set errors if there are validation errors
    if (newErrors.length > 0) {
      console.log('🟡 Setting validation errors:', newErrors);
      setErrorsWithDebug(newErrors, 'validateForm');
    }
    // Don't clear errors if validation passes - let login errors persist
    return newErrors.length === 0;
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setErrorsWithDebug(['Bitte geben Sie Ihre E-Mail-Adresse ein.'], 'resend_no_email');
      return;
    }

    try {
      const { BookingSystemAPI } = await import('../../services/BookingSystemAPI');
      const api = new BookingSystemAPI();
      const result = await api.resendVerificationEmail(formData.email);

      if (result.success) {
        setErrorsWithDebug([], 'resend_success');
        alert('✅ ' + result.message);
        setShowResendButton(false);
      } else {
        setErrorsWithDebug([result.message], 'resend_error');
      }
    } catch (error: any) {
      setErrorsWithDebug([error.message || 'Fehler beim Versenden der Email'], 'resend_exception');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    // Don't clear errors here - let them stay from validation or previous attempts
    // setErrors([]); // ❌ REMOVED - This was overriding our login error messages!
    
    try {
      console.log('Login attempt for:', formData.email);
      const response = await login(formData.email, formData.password);
      console.log('Login response:', response);
      
      if (response.success) {
        // Backend sends role as number: Customer=1, Staff=2, Admin=3
        const userRole = response.user?.Role || response.user?.role;
        console.log('Login successful, user role:', userRole);
        
        // Navigate based on user role using React Router
        // Use == for type coercion since role can be number or string
        // eslint-disable-next-line eqeqeq
        if (userRole == 3 || userRole === 'Admin') {
          console.log('Redirecting to admin dashboard...');
          // Clear errors on successful login
          setErrorsWithDebug([], 'successful_login');
          navigate('/admin');
        } else if (userRole == 5 || userRole === 'Accounting') {
          // Buchhaltungsrolle (Anlage v1.4 Abschnitt 2.7 Punkt 9)
          console.log('Redirecting accounting user to invoices...');
          setErrorsWithDebug([], 'successful_login');
          navigate('/admin/invoices');
        } else {
          console.log('Redirecting to customer dashboard...');
          // Clear errors on successful login
          setErrorsWithDebug([], 'successful_login');
          navigate('/dashboard');
        }
      } else {
        console.log('Login failed:', response);
        // Build error messages
        let errorMessages: string[] = [];

        if (response.errorCode === 'EMAIL_VERIFICATION_REQUIRED' || response.requiresEmailVerification || response.message?.includes('E-Mail-Adresse muss vor der Anmeldung verifiziert werden')) {
          errorMessages = ['Bitte verifizieren Sie zuerst Ihre E-Mail-Adresse.'];
          setShowResendButton(true);
        } else if (response.message?.includes('gesperrt')) {
          errorMessages = [response.message];
        } else if (response.message?.includes('Ungültige Anmeldedaten')) {
          errorMessages = ['Ungültige E-Mail-Adresse oder falsches Passwort.'];
        } else if (response.errors && response.errors.length > 0) {
          errorMessages = response.errors;
        } else if (response.message) {
          errorMessages = [response.message];
        } else {
          errorMessages = ['Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Daten.'];
        }
        
        console.log('Setting error messages:', errorMessages);
        // Use flushSync to ensure immediate state update
        flushSync(() => {
          setErrorsWithDebug(errorMessages, 'login_response_error');
        });
        console.log('🔵 Errors set, now setting isSubmitting to false...');
        setIsSubmitting(false);
        console.log('🔵 isSubmitting set to false. Errors should still be:', errorMessages);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      // Handle errors from the API
      const errorMsg = error?.message?.includes('E-Mail-Adresse muss vor der Anmeldung verifiziert werden')
        ? 'E-Mail-Adresse muss vor der Anmeldung verifiziert werden. Bitte prüfen Sie Ihr E-Mail-Postfach.'
        : error?.message?.includes('Ungültige Anmeldedaten')
        ? 'Ungültige E-Mail-Adresse oder falsches Passwort.'
        : error?.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Daten.';
      
      console.log('🔴 Setting error in catch block:', errorMsg);
      flushSync(() => {
        setErrorsWithDebug([errorMsg], 'catch_block');
      });
      console.log('🔵 Error set in catch, now setting isSubmitting to false...');
      setIsSubmitting(false);
      console.log('🔵 isSubmitting set to false in catch. Error should still be:', errorMsg);
    }
  };

  // DEBUG: Log render state (reduced verbosity)
  console.log('🟢 RENDER: errors=', errors.length > 0 ? `${errors.length} errors` : 'no errors');
  if (errors.length > 0) {
    console.log('🔍 About to render error box with', errors.length, 'errors');
  }

  return (
    <div className="login-form-container">
      <div className="login-form-card">
        {/* Logo über dem Formular */}
        <div className="login-logo-container">
          <img
            src="/logo-frankenfloss.svg"
            alt="Frankenfloss Logo"
            className="login-logo"
          />
        </div>

        <div className="login-form-header">
          <h2>Anmelden</h2>
          <p>Willkommen zurück bei Frankenfloss</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {errors.length > 0 && (
            <div className="error-messages">
              {errors.map((error, index) => (
                <div key={`error-${Date.now()}-${index}`} className="error-message">
                  <span>{error}</span>
                </div>
              ))}
              {showResendButton && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="resend-verification-button"
                >
                  Verifizierungs-E-Mail erneut senden
                </button>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">E-Mail-Adresse</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="ihre@email.de"
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Passwort</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Ihr Passwort"
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? 'Anmeldung läuft...' : 'Anmelden'}
          </button>
        </form>

        <div className="login-form-footer">
          <p>
            Noch kein Konto?{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/register')}
            >
              Jetzt registrieren
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;